import { useState, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, MapPin, Clock, MessageSquare, User, Eye, EyeOff, ChevronDown, ChevronRight, AlertTriangle, Timer } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { formatDistanceToNow, format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useLanguage } from '@/hooks/useLanguage';
import { cn } from '@/lib/utils';
import { getCompatibleTags } from '@/lib/d2dTagRules';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Working hours type
interface WorkingBlock {
  start: string | null;
  end: string | null;
  enabled?: boolean;
}

interface DayWorkingHours {
  block1: WorkingBlock;
  block2: WorkingBlock;
}

interface WorkingHours {
  monday: DayWorkingHours;
  tuesday: DayWorkingHours;
  wednesday: DayWorkingHours;
  thursday: DayWorkingHours;
  friday: DayWorkingHours;
  saturday: DayWorkingHours;
  sunday: DayWorkingHours;
}

// Utility function to abbreviate street names
const abbreviateStreetName = (name: string): string => {
  return name.replace(/straße$/i, 'Str.');
};

interface LeadUnit {
  id: string;
  unit_name: string;
  tags: string[];
  last_contact_at: string | null;
  next_reminder: string | null;
  created_at: string;
  house: {
    id: string;
    house_number: string;
    street: {
      id: string;
      name: string;
      plz: string;
      area: {
        id: string;
        name: string;
      };
    };
  };
  notes: Array<{
    id: string;
    note: string;
    created_at: string;
  }>;
}

interface D2DLeadsListProps {
  leadType: 'hot' | 'followup' | 'na';
  onBack: () => void;
  onNavigateToUnit?: (areaId: string, streetId: string, houseId: string, unitId: string) => void;
  onTagChange?: () => void; // Called when a tag is changed to refresh parent counts
}

export const D2DLeadsList = ({ leadType, onBack, onNavigateToUnit, onTagChange }: D2DLeadsListProps) => {
  const { getLocale } = useLanguage();
  const { user } = useAuth();
  const [leads, setLeads] = useState<LeadUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Notes visibility state
  const [showNotesGlobally, setShowNotesGlobally] = useState(() => {
    const saved = localStorage.getItem(`d2d-${leadType}-show-notes-globally`);
    return saved === null ? true : saved === 'true';
  });
  
  const [unitNotesVisibility, setUnitNotesVisibility] = useState<{ [unitId: string]: boolean }>({});
  
  // Street and house folder visibility state
  const [streetFolderVisibility, setStreetFolderVisibility] = useState<{ [streetKey: string]: boolean }>({});
  const [houseFolderVisibility, setHouseFolderVisibility] = useState<{ [houseKey: string]: boolean }>({});
  const [isProcessingOverdue, setIsProcessingOverdue] = useState(false);
  
  // Confirmation dialog state for KI tag
  const [showKIConfirmDialog, setShowKIConfirmDialog] = useState(false);
  const [pendingKIUnitId, setPendingKIUnitId] = useState<string | null>(null);

  // Working block filter state
  const [filterByWorkingBlock, setFilterByWorkingBlock] = useState(() => {
    const saved = localStorage.getItem(`d2d-${leadType}-filter-working-block`);
    return saved !== 'false'; // Default to true
  });
  const [workingHours, setWorkingHours] = useState<WorkingHours | null>(null);
  const [userTimezone, setUserTimezone] = useState<string>('Europe/Berlin');

  // Load user working hours
  useEffect(() => {
    const loadWorkingHours = async () => {
      if (!user?.id) return;
      
      const { data } = await supabase
        .from('profiles')
        .select('working_hours, timezone')
        .eq('user_id', user.id)
        .single();
      
      if (data?.working_hours) {
        setWorkingHours(data.working_hours as unknown as WorkingHours);
      }
      if (data?.timezone) {
        setUserTimezone(data.timezone);
      }
    };
    
    loadWorkingHours();
  }, [user?.id]);

  // Get current working block info
  const currentWorkingBlockInfo = useMemo(() => {
    if (!workingHours) return { inBlock: false, blockName: '', blockEnd: '' };
    
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const dayName = dayNames[now.getDay()];
    const dayHours = workingHours[dayName];
    
    if (!dayHours) return { inBlock: false, blockName: '', blockEnd: '' };
    
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTotalMinutes = currentHour * 60 + currentMinute;
    
    // Check Block 1
    if (dayHours.block1?.start && dayHours.block1?.end && dayHours.block1?.enabled !== false) {
      const [startH, startM] = dayHours.block1.start.split(':').map(Number);
      const [endH, endM] = dayHours.block1.end.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      
      if (currentTotalMinutes >= startMinutes && currentTotalMinutes < endMinutes) {
        return { inBlock: true, blockName: 'Vormittag', blockEnd: dayHours.block1.end };
      }
    }
    
    // Check Block 2
    if (dayHours.block2?.start && dayHours.block2?.end && dayHours.block2?.enabled !== false) {
      const [startH, startM] = dayHours.block2.start.split(':').map(Number);
      const [endH, endM] = dayHours.block2.end.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      
      if (currentTotalMinutes >= startMinutes && currentTotalMinutes < endMinutes) {
        return { inBlock: true, blockName: 'Nachmittag', blockEnd: dayHours.block2.end };
      }
    }
    
    return { inBlock: false, blockName: '', blockEnd: '' };
  }, [workingHours]);

  // Filter leads by current working block
  const filteredLeadsByWorkingBlock = useMemo(() => {
    if (!filterByWorkingBlock || !workingHours || leadType !== 'followup') {
      return leads;
    }
    
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const dayName = dayNames[now.getDay()];
    const dayHours = workingHours[dayName];
    
    if (!dayHours) return leads;
    
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTotalMinutes = currentHour * 60 + currentMinute;
    
    // Determine current block boundaries
    let blockStart: number | null = null;
    let blockEnd: number | null = null;
    
    // Check Block 1
    if (dayHours.block1?.start && dayHours.block1?.end && dayHours.block1?.enabled !== false) {
      const [startH, startM] = dayHours.block1.start.split(':').map(Number);
      const [endH, endM] = dayHours.block1.end.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      
      if (currentTotalMinutes >= startMinutes && currentTotalMinutes < endMinutes) {
        blockStart = startMinutes;
        blockEnd = endMinutes;
      }
    }
    
    // Check Block 2
    if (blockStart === null && dayHours.block2?.start && dayHours.block2?.end && dayHours.block2?.enabled !== false) {
      const [startH, startM] = dayHours.block2.start.split(':').map(Number);
      const [endH, endM] = dayHours.block2.end.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      
      if (currentTotalMinutes >= startMinutes && currentTotalMinutes < endMinutes) {
        blockStart = startMinutes;
        blockEnd = endMinutes;
      }
    }
    
    // If not in any block, show all
    if (blockStart === null || blockEnd === null) {
      return leads;
    }
    
    // Filter leads with next_reminder within current block
    return leads.filter(lead => {
      // Always include KT leads without reminder
      if (lead.tags?.includes('KT') && !lead.next_reminder) {
        return true;
      }
      
      if (!lead.next_reminder) return false;
      
      const reminderDate = new Date(lead.next_reminder);
      const reminderHour = reminderDate.getHours();
      const reminderMinute = reminderDate.getMinutes();
      const reminderTotalMinutes = reminderHour * 60 + reminderMinute;
      
      // Check if reminder is today and within current block
      const isToday = reminderDate.toDateString() === now.toDateString();
      const isOverdue = reminderDate < now;
      
      // Include overdue leads
      if (isOverdue) return true;
      
      // Include if today and within block
      if (isToday && reminderTotalMinutes >= blockStart && reminderTotalMinutes < blockEnd) {
        return true;
      }
      
      return false;
    });
  }, [leads, filterByWorkingBlock, workingHours, leadType]);

  // Toggle working block filter
  const toggleWorkingBlockFilter = () => {
    const newValue = !filterByWorkingBlock;
    setFilterByWorkingBlock(newValue);
    localStorage.setItem(`d2d-${leadType}-filter-working-block`, newValue.toString());
    
    if (newValue) {
      if (currentWorkingBlockInfo.inBlock) {
        toast.success('Arbeitsblock-Filter aktiv', {
          description: `Zeige nur Leads für ${currentWorkingBlockInfo.blockName} (bis ${currentWorkingBlockInfo.blockEnd})`
        });
      } else {
        toast.info('Kein aktiver Arbeitsblock', {
          description: 'Du befindest dich aktuell außerhalb deiner Arbeitszeiten. Alle Leads werden angezeigt.'
        });
      }
    } else {
      toast.success('Arbeitsblock-Filter deaktiviert', {
        description: 'Alle Follow-ups werden angezeigt'
      });
    }
  };

  // Toggle global notes visibility
  const toggleGlobalNotesVisibility = () => {
    const newValue = !showNotesGlobally;
    setShowNotesGlobally(newValue);
    localStorage.setItem(`d2d-${leadType}-show-notes-globally`, newValue.toString());
  };

  // Toggle individual unit notes visibility
  const toggleUnitNotesVisibility = (unitId: string) => {
    setUnitNotesVisibility(prev => ({
      ...prev,
      [unitId]: !prev[unitId]
    }));
  };

  // Toggle street folder visibility
  const toggleStreetFolderVisibility = (streetKey: string) => {
    setStreetFolderVisibility(prev => ({
      ...prev,
      [streetKey]: !prev[streetKey]
    }));
  };

  // Toggle house folder visibility
  const toggleHouseFolderVisibility = (houseKey: string) => {
    setHouseFolderVisibility(prev => ({
      ...prev,
      [houseKey]: !prev[houseKey]
    }));
  };

  // Process overdue follow-ups
  const processOverdueFollowUps = async () => {
    if (isProcessingOverdue || !user) return;
    
    setIsProcessingOverdue(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No active session');
        return;
      }

      const { data, error } = await supabase.functions.invoke('update-overdue-followups', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error processing overdue follow-ups:', error);
      } else {
        console.log('Processed overdue follow-ups:', data);
        // Refresh the leads list after processing
        loadLeads();
      }
    } catch (error) {
      console.error('Error calling update-overdue-followups function:', error);
    } finally {
      setIsProcessingOverdue(false);
    }
  };

  const getTitle = () => {
    switch (leadType) {
      case 'hot':
        return 'Hot Leads';
      case 'followup':
        return 'Follow-ups';
      case 'na':
        return 'Nicht Angetroffen';
      default:
        return 'Leads';
    }
  };

  const getDescription = () => {
    switch (leadType) {
      case 'hot':
        return 'Wohneinheiten mit hohem Interesse oder kürzlichen positiven Kontakten';
      case 'followup':
        return 'Wohneinheiten mit fälligen Terminen und Erinnerungen';
      case 'na':
        return 'nochmal versuchen';
      default:
        return '';
    }
  };

  const loadLeads = async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);
    try {
      // Step 1: Get active areas in parallel
      const [ownAreasResult, sharesResult] = await Promise.all([
        supabase
          .from('d2d_areas')
          .select('id')
          .eq('created_by', user.id)
          .eq('is_active', true),
        supabase
          .from('d2d_area_shares')
          .select('area_id')
          .eq('shared_with', user.id)
          .or('is_active.eq.true,is_active.is.null')
      ]);
      
      // Combine active area IDs
      const activeAreaIds: string[] = [
        ...(ownAreasResult.data || []).map(a => a.id),
        ...(sharesResult.data || []).map(s => s.area_id)
      ];
      
      console.log('D2DLeadsList: Active area IDs:', activeAreaIds);
      
      // If no active areas, show empty state
      if (activeAreaIds.length === 0) {
        setLeads([]);
        setLoading(false);
        return;
      }
      
      // Build optimized query with server-side tag filtering
      let query = supabase
        .from('d2d_units')
        .select(`
          id,
          unit_name,
          tags,
          last_contact_at,
          next_reminder,
          created_at,
          house:d2d_houses!inner(
            id,
            house_number,
            street:d2d_streets!inner(
              id,
              name,
              plz,
              area_id,
              area:d2d_areas!inner(
                id,
                name,
                is_active
              )
            )
          ),
          notes:d2d_notes(
            id,
            note,
            created_at
          )
        `)
        .in('d2d_houses.d2d_streets.area_id', activeAreaIds);
      
      // Add server-side tag filter based on lead type
      if (leadType === 'hot') {
        query = query.contains('tags', ['HOT']);
      } else if (leadType === 'na') {
        query = query.contains('tags', ['NA']);
      } else if (leadType === 'followup') {
        // For follow-ups, we need KT tags or units with next_reminder
        // Can't do complex OR on array + date, so filter client-side but limit results
        query = query.or('tags.cs.{"KT"},next_reminder.not.is.null');
      }
      
      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(500); // Limit to prevent huge payloads

      if (error) throw error;

      // Filter based on lead type with safe date handling
      const filteredLeads = (data || []).filter((unit: any) => {
        try {
          // Check if unit has required nested data structure
          if (!unit?.house?.street?.area) {
            console.warn('Unit missing required nested data:', unit?.id);
            return false;
          }
          
          switch (leadType) {
            case 'hot':
              return unit.tags?.includes('HOT');
            case 'followup':
              // Exclude units with NA, KI, WIN or NO tags from follow-ups
              if (unit.tags?.includes('NA') || unit.tags?.includes('KI') || unit.tags?.includes('WIN') || unit.tags?.includes('NO')) return false;
              
              // Include all units with KT tag (they all need follow-up)
              if (unit.tags?.includes('KT')) {
                return true;
              }
              
              // For all units (including Hot Leads), check if they have due reminders
              if (!unit.next_reminder || !isValidDate(unit.next_reminder)) return false;
              
              const reminderDate = new Date(unit.next_reminder);
              const today = new Date();
              const tomorrow = new Date(today);
              tomorrow.setDate(tomorrow.getDate() + 1);
              
              // Show reminders due today, tomorrow, or overdue
              const isDueToday = reminderDate.toDateString() === today.toDateString();
              const isDueTomorrow = reminderDate.toDateString() === tomorrow.toDateString();
              const isOverdue = reminderDate <= today;
              
              return isDueToday || isDueTomorrow || isOverdue;
            case 'na':
              if (!unit.tags?.includes('NA')) return false;
              
              // Time-based visibility: units tagged NA in the morning reappear in the afternoon
              if (unit.last_contact_at && isValidDate(unit.last_contact_at)) {
                const lastContactDate = new Date(unit.last_contact_at);
                const now = new Date();
                const isToday = lastContactDate.toDateString() === now.toDateString();
                
                if (isToday) {
                  const taggedHour = lastContactDate.getHours();
                  const currentHour = now.getHours();
                  const morningCutoff = 12; // 12:00 Uhr als Grenze
                  
                  // Wenn vormittags getaggt (vor 12:00) und jetzt noch vormittags → ausblenden
                  if (taggedHour < morningCutoff && currentHour < morningCutoff) {
                    return false;
                  }
                }
              }
              return true;
            default:
              return false;
          }
        } catch (filterError) {
          console.error('Error filtering unit:', unit.id, filterError);
          return false;
        }
      });

      setLeads(filteredLeads);
    } catch (error) {
      console.error('Error loading leads:', error);
      setError(error instanceof Error ? error.message : 'Fehler beim Laden der Leads');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to validate dates
  const isValidDate = (dateString: string | null): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  };

  useEffect(() => {
    let mounted = true;
    
    const loadData = async () => {
      if (mounted) {
        await loadLeads();
        
        // Auto-process overdue follow-ups if this is the followup view
        if (leadType === 'followup' && mounted) {
          setTimeout(() => {
            if (mounted) {
              processOverdueFollowUps();
            }
          }, 1000); // Small delay to let the UI render first
        }
      }
    };
    
    loadData();
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      mounted = false;
    };
  }, [user?.id, leadType]);

  const getTagColor = (tag: string) => {
    switch (tag) {
      case 'HOT':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'KI':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'NA':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'INT':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'TERMIN':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatLastContact = (lastContact: string | null) => {
    if (!lastContact) return 'Kein Kontakt';
    
    try {
      return formatDistanceToNow(new Date(lastContact), { 
        addSuffix: true, 
        locale: getLocale() 
      });
    } catch {
      return 'Unbekannt';
    }
  };

  // Handle NA tag - add tag and create follow-up
  const handleNATag = async (unitId: string, currentTags: string[]) => {
    // Ensure currentTags is always an array
    const safeTags = Array.isArray(currentTags) ? currentTags : [];
    
    // In the leads list, clicking NA should ALWAYS add/keep NA and create follow-up
    // Not toggle it off like in the unit detail view
    let newTags: string[];
    if (safeTags.includes('NA')) {
      // Already has NA - keep all tags, just update timestamp and create new follow-up
      newTags = safeTags;
    } else {
      // Apply NA tag using centralized rules
      newTags = getCompatibleTags(safeTags as any[], 'NA' as any);
    }
    const now = new Date().toISOString();
    
    // Optimistic update - remove unit from list immediately (it now has a follow-up scheduled)
    setLeads(prevLeads => prevLeads.filter(lead => lead.id !== unitId));

    try {
      // Update tags and last_contact_at in database
      const { error: updateError } = await supabase
        .from('d2d_units')
        .update({ 
          tags: newTags,
          last_contact_at: now
        })
        .eq('id', unitId);

      if (updateError) throw updateError;

      // Create follow-up via edge function
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const { error: followUpError } = await supabase.functions.invoke('smart-followup-scheduling', {
        body: { unit_id: unitId, visit_result: 'NA' },
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (followUpError) throw followUpError;

      toast.success('NA Tag gesetzt', {
        description: 'Follow-up wurde automatisch erstellt'
      });
      
      // Notify parent to refresh counts
      onTagChange?.();
    } catch (error) {
      console.error('Error setting NA tag:', error);
      // Revert optimistic update on error - add unit back
      const originalUnit = leads.find(l => l.id === unitId);
      if (originalUnit) {
        setLeads(prevLeads => [...prevLeads, originalUnit]);
      }
      toast.error('Fehler', {
        description: 'NA Tag konnte nicht gesetzt werden'
      });
    }
  };

  // Handle KI tag - show confirmation dialog
  const handleKITag = (unitId: string) => {
    console.log('handleKITag called:', { unitId });
    setPendingKIUnitId(unitId);
    setShowKIConfirmDialog(true);
  };

  // Confirm KI tag - apply tag and remove from list
  const confirmKITag = async () => {
    if (!pendingKIUnitId) return;

    const unit = leads.find(l => l.id === pendingKIUnitId);
    if (!unit) return;

    try {
      // Apply KI tag using centralized rules
      const newTags = getCompatibleTags(unit.tags as any[], 'KI' as any);
      
      // Update tags in database
      const { error: updateError } = await supabase
        .from('d2d_units')
        .update({ tags: newTags })
        .eq('id', pendingKIUnitId);

      if (updateError) throw updateError;

      // Remove from local state immediately (optimistic update)
      setLeads(prevLeads => prevLeads.filter(l => l.id !== pendingKIUnitId));

      toast.success('KI Tag gesetzt', {
        description: 'Unit wurde als "Kein Interesse" markiert'
      });
      
      // Notify parent to refresh counts
      onTagChange?.();
    } catch (error) {
      console.error('Error setting KI tag:', error);
      toast.error('Fehler', {
        description: 'KI Tag konnte nicht gesetzt werden'
      });
      // Reload on error to get accurate state
      await loadLeads();
    } finally {
      setPendingKIUnitId(null);
      setShowKIConfirmDialog(false);
    }
  };

  // Error state
  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack} className="hover:bg-transparent border border-border rounded-sm">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <p className="text-sm text-muted-foreground">Fehler beim Laden</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleGlobalNotesVisibility}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            {showNotesGlobally ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            Notizen
          </Button>
        </div>
        
        <Card className="p-8 text-center">
          <div className="mx-auto h-12 w-12 text-destructive mb-4">
            <User className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Fehler</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => { setError(null); loadLeads(); }} variant="outline">
            Erneut versuchen
          </Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack} className="hover:bg-transparent border border-border rounded-sm">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <p className="text-sm text-muted-foreground">Lade Daten...</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleGlobalNotesVisibility}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            {showNotesGlobally ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            Notizen
          </Button>
        </div>
        
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <p className="text-sm text-muted-foreground mb-2 text-center">{getDescription()}</p>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="hover:bg-transparent border border-border rounded-sm">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={toggleWorkingBlockFilter}
            className={cn(
              "flex items-center justify-center gap-2 py-2 px-4 rounded-xl transition-all duration-300 min-w-[100px]",
              filterByWorkingBlock 
                ? "bg-gradient-to-br from-primary/10 via-background to-accent/10 border-2 border-primary/30 shadow-md backdrop-blur-sm" 
                : "hover:bg-transparent border border-border text-muted-foreground hover:text-foreground"
            )}
          >
            <Timer className={cn("h-4 w-4", filterByWorkingBlock && "text-primary")} />
            <span className={cn("text-sm", filterByWorkingBlock && "font-medium bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent")}>
              {filterByWorkingBlock && currentWorkingBlockInfo.inBlock 
                ? `bis ${currentWorkingBlockInfo.blockEnd}` 
                : "Zeitblock"}
            </span>
          </button>
          <button 
            onClick={toggleGlobalNotesVisibility}
            className={cn(
              "flex items-center justify-center gap-2 py-2 px-4 rounded-xl transition-all duration-300 min-w-[100px]",
              showNotesGlobally
                ? "bg-gradient-to-br from-primary/10 via-background to-accent/10 border-2 border-primary/30 shadow-md backdrop-blur-sm"
                : "hover:bg-transparent border border-border text-muted-foreground hover:text-foreground"
            )}
          >
            {showNotesGlobally ? <Eye className="h-4 w-4 text-primary" /> : <EyeOff className="h-4 w-4" />}
            <span className={cn("text-sm", showNotesGlobally && "font-medium bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent")}>
              Notizen
            </span>
          </button>
        </div>
      </div>

      <Separator className="mb-6" />

      {/* Results count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {filteredLeadsByWorkingBlock.length} {filteredLeadsByWorkingBlock.length === 1 ? 'Lead gefunden' : 'Leads gefunden'}
          {filterByWorkingBlock && filteredLeadsByWorkingBlock.length !== leads.length && (
            <span className="ml-1">({leads.length} gesamt)</span>
          )}
        </p>
      </div>

      {/* Leads list */}
      {filteredLeadsByWorkingBlock.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="mx-auto h-12 w-12 text-muted-foreground mb-4">
            <User className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Keine {getTitle()} gefunden</h3>
          <p className="text-muted-foreground">
            {filterByWorkingBlock && leads.length > 0
              ? 'Im aktuellen Arbeitsblock gibt es keine Leads. Deaktiviere den Filter, um alle zu sehen.'
              : 'Aktuell gibt es keine Wohneinheiten, die diesem Kriterium entsprechen.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {(() => {
            // Sort leads first by street name alphabetically, then by house number
            const sortedLeads = [...filteredLeadsByWorkingBlock].sort((a, b) => {
              const streetCompare = a.house.street.name.localeCompare(b.house.street.name, 'de');
              if (streetCompare !== 0) return streetCompare;
              
              // Sort by house number - handle numbers properly
              const aNum = parseInt(a.house.house_number) || 0;
              const bNum = parseInt(b.house.house_number) || 0;
              return aNum - bNum;
            });

            // First level: Group leads by street
            const streetGroupedLeads = sortedLeads.reduce((streets, lead) => {
              const streetKey = `${lead.house.street.id}`;
              if (!streets[streetKey]) {
                streets[streetKey] = {
                  streetName: lead.house.street.name,
                  plz: lead.house.street.plz,
                  leads: []
                };
              }
              streets[streetKey].leads.push(lead);
              return streets;
            }, {} as { [key: string]: { streetName: string; plz: string; leads: LeadUnit[] } });

            return Object.entries(streetGroupedLeads).map(([streetKey, streetData]) => {
              const isStreetExpanded = streetFolderVisibility[streetKey] === true;
              const totalUnitsInStreet = streetData.leads.length;

              // Second level: Group leads within street by house number
              const houseGroupedLeads = streetData.leads.reduce((houses, lead) => {
                const houseKey = `${streetKey}-${lead.house.id}`;
                if (!houses[houseKey]) {
                  houses[houseKey] = {
                    houseNumber: lead.house.house_number,
                    houseId: lead.house.id,
                    leads: []
                  };
                }
                houses[houseKey].leads.push(lead);
                return houses;
              }, {} as { [key: string]: { houseNumber: string; houseId: string; leads: LeadUnit[] } });

              return (
                <div key={streetKey} className="space-y-2">
                  {/* Street folder header */}
                  <Card 
                    className="p-3 cursor-pointer hover:bg-muted/30 border-l-4 border-l-primary/50" 
                    onClick={() => toggleStreetFolderVisibility(streetKey)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          {isStreetExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-primary" />
                          <div className="font-medium">
                            {abbreviateStreetName(streetData.streetName)}
                          </div>
                        </div>
                      </div>
                      <Badge 
                        variant="outline" 
                        className="text-xs sm:text-sm font-medium px-2 py-1 rounded-full bg-primary/10 border-primary/30 text-primary whitespace-nowrap shrink-0"
                      >
                        {totalUnitsInStreet} {totalUnitsInStreet === 1 ? 'Einheit' : 'Einheiten'}
                      </Badge>
                    </div>
                  </Card>

                  {/* Houses within street */}
                  {isStreetExpanded && (
                    <div className="ml-4 space-y-2">
                      {Object.entries(houseGroupedLeads).map(([houseKey, houseData]) => {
                        const hasMultipleUnits = houseData.leads.length > 1;
                        const isHouseExpanded = houseFolderVisibility[houseKey] === true;
                        const firstLead = houseData.leads[0];

                        if (!hasMultipleUnits) {
                          // Single unit in house - display directly
                          const lead = firstLead;
                          const isHot = lead.tags?.includes('HOT');
                          const isOverdue = lead.next_reminder && isValidDate(lead.next_reminder) && new Date(lead.next_reminder) < new Date();
                          const isNA = lead.tags?.includes('NA');

                          return (
                            <Card 
                              key={lead.id} 
                              className={cn(
                                "p-4 transition-all duration-300 cursor-pointer hover:shadow-md hover:border-primary/30 bg-card border-border",
                                isHot && "ring-2 ring-destructive/30 bg-destructive/10 animate-border-travel",
                                !isHot && isOverdue && "ring-2 ring-orange-400/30 bg-orange-50 dark:bg-orange-950/20",
                                isNA && "bg-muted/50 border-muted text-muted-foreground opacity-70"
                              )}
                              onClick={() => {
                                if (onNavigateToUnit) {
                                  onNavigateToUnit(
                                    lead.house.street.area.id,
                                    lead.house.street.id,
                                    lead.house.id,
                                    lead.id
                                  );
                                }
                              }}
                            >
                              <div className="space-y-3">
                                {/* Header */}
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="space-y-2">
                                       {isHot && (
                                         <Badge variant="destructive" className="text-xs w-fit">
                                           🔥 HOT
                                         </Badge>
                                       )}
                                       
                                       <div className="flex items-center gap-2">
                                         <span className="font-medium">Nr. {lead.house.house_number}</span>
                                         {lead.unit_name && (
                                           <span className="text-sm text-muted-foreground">
                                             - {lead.unit_name}
                                           </span>
                                         )}
                                       </div>
                                       
                                       <div className="flex items-center gap-2">
                                         {lead.notes && lead.notes.length > 0 && (
                                           <Badge variant="outline" className="text-xs">
                                             {lead.notes.length} {lead.notes.length === 1 ? 'Notiz' : 'Notizen'}
                                           </Badge>
                                         )}
                                         {!showNotesGlobally && lead.notes && lead.notes.length > 0 && (
                                           <Button
                                             variant="ghost"
                                             size="sm"
                                             onClick={(e) => {
                                               e.stopPropagation();
                                               toggleUnitNotesVisibility(lead.id);
                                             }}
                                             className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                                           >
                                             {unitNotesVisibility[lead.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                           </Button>
                                         )}
                                       </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Tags */}
                                {lead.tags && lead.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {[...new Set(lead.tags)].map((tag, index) => (
                                      <Badge 
                                        key={index} 
                                        variant="secondary" 
                                        className={`text-xs ${getTagColor(tag)}`}
                                      >
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                )}

                                {/* Quick Action Buttons */}
                                <div className="flex gap-2 mt-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleNATag(lead.id, lead.tags);
                                    }}
                                    className="flex-1 h-8 text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
                                  >
                                    ❌ NA
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleKITag(lead.id);
                                    }}
                                    className="flex-1 h-8 text-xs bg-red-50 hover:bg-red-100 dark:bg-red-950 dark:hover:bg-red-900 text-red-700 dark:text-red-300"
                                  >
                                    🚫 KI
                                  </Button>
                                </div>

                                {/* Notes */}
                                {lead.notes && lead.notes.length > 0 && (showNotesGlobally || unitNotesVisibility[lead.id]) && (
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                      <h5 className="text-sm font-medium text-muted-foreground">Notizen:</h5>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleUnitNotesVisibility(lead.id);
                                        }}
                                        className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                                      >
                                        {unitNotesVisibility[lead.id] !== false ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                      </Button>
                                    </div>
                                    {unitNotesVisibility[lead.id] !== false && (
                                      <div className="space-y-1">
                                        {lead.notes.map((note) => (
                                          <div key={note.id} className="bg-muted/30 rounded-md p-2 text-sm">
                                            <p className="mb-1">{note.note}</p>
                                            <p className="text-xs text-muted-foreground">
                                              {formatDistanceToNow(new Date(note.created_at), { 
                                                addSuffix: true, 
                                                 locale: getLocale() 
                                              })}
                                            </p>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Last contact info and Reminder */}
                                <div className="flex items-center justify-between">
                                  {lead.last_contact_at && (
                                    <div className="text-xs text-muted-foreground">
                                      <Clock className="h-3 w-3 inline mr-1" />
                                      Letzter Kontakt: {formatLastContact(lead.last_contact_at)}
                                    </div>
                                  )}
                                  
                                  {lead.next_reminder && (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                      <Clock className="h-4 w-4" />
                                      <span>
                                        {isOverdue ? 'Überfällig: ' : 'Follow-Up: '}
                                         {format(new Date(lead.next_reminder), 'dd.MM.yyyy HH:mm', { locale: getLocale() })}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Card>
                          );
                        } else {
                          // Multiple units in house - create collapsible folder
                          return (
                            <div key={houseKey} className="space-y-2">
                              {/* House folder header */}
                              <Card 
                                className="p-3 cursor-pointer hover:bg-muted/30" 
                                onClick={() => toggleHouseFolderVisibility(houseKey)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                      {isHouseExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </div>
                                    <div className="font-medium">
                                      Nr. {houseData.houseNumber}
                                    </div>
                                  </div>
                                  <Badge 
                                    variant="outline" 
                                    className="text-xs sm:text-sm font-medium px-2 py-1 rounded-full bg-muted/20 border-muted-foreground/30"
                                  >
                                    {houseData.leads.length} Einheiten
                                  </Badge>
                                </div>
                              </Card>

                              {/* Units in house folder */}
                              {isHouseExpanded && (
                                <div className="ml-4 space-y-2">
                                  {houseData.leads.map((lead) => {
                                    const isHot = lead.tags?.includes('HOT');
                                    const isOverdue = lead.next_reminder && isValidDate(lead.next_reminder) && new Date(lead.next_reminder) < new Date();
                                    const isNA = lead.tags?.includes('NA');

                                    return (
                                      <Card 
                                        key={lead.id} 
                                        className={cn(
                                          "p-3 transition-all duration-300 cursor-pointer hover:shadow-lg hover:border-primary/50 bg-background border-2 border-border/60 shadow-sm",
                                          isHot && "ring-2 ring-destructive/50 bg-destructive/15 border-destructive/40 animate-border-travel shadow-destructive/20",
                                          !isHot && isOverdue && "ring-2 ring-orange-400/50 bg-orange-100 dark:bg-orange-950/30 border-orange-300 dark:border-orange-700",
                                          isNA && "bg-muted/30 border-muted-foreground/20 text-muted-foreground"
                                        )}
                                        onClick={() => {
                                          if (onNavigateToUnit) {
                                            onNavigateToUnit(
                                              lead.house.street.area.id,
                                              lead.house.street.id,
                                              lead.house.id,
                                              lead.id
                                            );
                                          }
                                        }}
                                      >
                                        <div className="space-y-2">
                                          {/* Unit header */}
                                          <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                              <div className="space-y-1">
                                                 {isHot && (
                                                   <Badge variant="destructive" className="text-xs w-fit">
                                                     🔥 HOT
                                                   </Badge>
                                                 )}
                                                 
                                                 <div className="font-medium text-sm">
                                                   {lead.unit_name || 'Einheit'}
                                                 </div>
                                                 
                                                 <div className="flex items-center gap-2">
                                                   {lead.notes && lead.notes.length > 0 && (
                                                     <Badge variant="outline" className="text-xs">
                                                       {lead.notes.length} {lead.notes.length === 1 ? 'Notiz' : 'Notizen'}
                                                     </Badge>
                                                   )}
                                                   {!showNotesGlobally && lead.notes && lead.notes.length > 0 && (
                                                     <Button
                                                       variant="ghost"
                                                       size="sm"
                                                       onClick={(e) => {
                                                         e.stopPropagation();
                                                         toggleUnitNotesVisibility(lead.id);
                                                       }}
                                                       className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
                                                     >
                                                       {unitNotesVisibility[lead.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                                     </Button>
                                                   )}
                                                 </div>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Tags */}
                                          {lead.tags && lead.tags.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                              {[...new Set(lead.tags)].map((tag, index) => (
                                                <Badge 
                                                  key={index} 
                                                  variant="secondary" 
                                                  className={`text-xs ${getTagColor(tag)}`}
                                                >
                                                  {tag}
                                                </Badge>
                                              ))}
                                            </div>
                                          )}

                                          {/* Quick Action Buttons */}
                                          <div className="flex gap-2 mt-2">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleNATag(lead.id, lead.tags);
                                              }}
                                              className="flex-1 h-7 text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
                                            >
                                              ❌ NA
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleKITag(lead.id);
                                              }}
                                              className="flex-1 h-7 text-xs bg-red-50 hover:bg-red-100 dark:bg-red-950 dark:hover:bg-red-900 text-red-700 dark:text-red-300"
                                            >
                                              🚫 KI
                                            </Button>
                                          </div>

                                          {/* Notes */}
                                          {lead.notes && lead.notes.length > 0 && (showNotesGlobally || unitNotesVisibility[lead.id]) && (
                                            <div className="space-y-1">
                                              <div className="flex items-center justify-between">
                                                <h5 className="text-xs font-medium text-muted-foreground">Notizen:</h5>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleUnitNotesVisibility(lead.id);
                                                  }}
                                                  className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
                                                >
                                                  {unitNotesVisibility[lead.id] !== false ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                                </Button>
                                              </div>
                                              {unitNotesVisibility[lead.id] !== false && (
                                                <div className="space-y-1">
                                                  {lead.notes.map((note) => (
                                                    <div key={note.id} className="bg-muted/30 rounded-md p-2 text-xs">
                                                      <p className="mb-1">{note.note}</p>
                                                      <p className="text-xs text-muted-foreground">
                                                        {formatDistanceToNow(new Date(note.created_at), { 
                                                          addSuffix: true, 
                                                           locale: getLocale() 
                                                        })}
                                                      </p>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          )}

                                          {/* Last contact info and Reminder */}
                                          <div className="flex items-center justify-between text-xs">
                                            {lead.last_contact_at && (
                                              <div className="text-muted-foreground">
                                                <Clock className="h-3 w-3 inline mr-1" />
                                                Letzter Kontakt: {formatLastContact(lead.last_contact_at)}
                                              </div>
                                            )}
                                            
                                             {lead.next_reminder && (
                                               <div className="flex items-center gap-1 text-muted-foreground">
                                                 {isOverdue && (
                                                   <AlertTriangle className="h-3 w-3 text-destructive animate-pulse" />
                                                 )}
                                                 <Clock className="h-3 w-3" />
                                                  <span className={isOverdue ? "text-destructive font-medium" : ""}>
                                                    {isOverdue ? 'Überfällig: ' : 'Follow-Up: '}
                                                    {format(new Date(lead.next_reminder), 'dd.MM.yyyy HH:mm', { locale: getLocale() })}
                                                  </span>
                                               </div>
                                             )}
                                          </div>
                                        </div>
                                      </Card>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        }
                      })}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}
      
      {/* KI Confirmation Dialog */}
      <AlertDialog open={showKIConfirmDialog} onOpenChange={setShowKIConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kein Interesse bestätigen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diesen Lead wirklich als "Kein Interesse" markieren? Die Unit wird aus dieser Ansicht entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setPendingKIUnitId(null);
              setShowKIConfirmDialog(false);
            }}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmKITag} className="bg-red-600 hover:bg-red-700">
              Bestätigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};